/**
 * Google Identity Services (GIS) loader + OAuth 2.0 "auth code" popup helper.
 *
 * We use the authorization-code flow (not the implicit token flow): the popup returns a
 * one-time `code` that the backend exchanges for tokens using the client secret. That keeps
 * the secret server-side and lets the backend read the user's Google Calendar to import events.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// Read-only access to the user's calendars is enough to import events.
export const GOOGLE_CALENDAR_READONLY_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

// Write access, needed to create the calendar entry that carries the Google Meet link.
export const GOOGLE_CALENDAR_EVENTS_SCOPE = 'https://www.googleapis.com/auth/calendar.events';

type CodeResponse = { code?: string; error?: string; error_description?: string };

type CodeClient = { requestCode: () => void };

type TokenResponse = { access_token?: string; error?: string; error_description?: string };

type TokenClient = { requestAccessToken: () => void };

type GoogleOAuth2 = {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    ux_mode?: 'popup' | 'redirect';
    callback: (response: CodeResponse) => void;
  }) => CodeClient;
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }) => TokenClient;
};

function getGoogleOAuth2(): GoogleOAuth2 | null {
  return (window as any)?.google?.accounts?.oauth2 ?? null;
}

/** Injects the GIS script once and resolves when `google.accounts.oauth2` is ready. */
export function loadGoogleIdentityServices(): Promise<GoogleOAuth2> {
  return new Promise((resolve, reject) => {
    const existing = getGoogleOAuth2();
    if (existing) {
      resolve(existing);
      return;
    }

    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i += 1) {
      if (scripts[i].src === GIS_SRC) {
        scripts[i].addEventListener('load', () => {
          const oauth2 = getGoogleOAuth2();
          return oauth2 ? resolve(oauth2) : reject(new Error('Google Identity Services failed to initialize.'));
        });
        return;
      }
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const oauth2 = getGoogleOAuth2();
      return oauth2 ? resolve(oauth2) : reject(new Error('Google Identity Services failed to initialize.'));
    };
    script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
    document.body.appendChild(script);
  });
}

/**
 * Opens the Google OAuth consent popup and resolves with the one-time authorization code.
 * Must be triggered from a user gesture (e.g. a button click) or the popup is blocked.
 */
export async function requestGoogleAuthCode(scope: string): Promise<string> {
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error('Missing REACT_APP_GOOGLE_OAUTH_CLIENT_ID.');
  }

  const oauth2 = await loadGoogleIdentityServices();

  return new Promise((resolve, reject) => {
    const codeClient = oauth2.initCodeClient({
      client_id: clientId,
      scope,
      ux_mode: 'popup',
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        if (!response.code) {
          reject(new Error('No authorization code returned from Google.'));
          return;
        }
        resolve(response.code);
      },
    });
    codeClient.requestCode();
  });
}

/**
 * Opens the Google OAuth consent popup and resolves with a short-lived access token.
 *
 * Unlike `requestGoogleAuthCode`, this is the implicit token flow: the token stays in the
 * browser and is used to call the Calendar API directly, so no client secret and no backend
 * exchange are involved. Use it only for one-off calls made straight from the browser.
 * Must be triggered from a user gesture (e.g. a button click) or the popup is blocked.
 */
export async function requestGoogleAccessToken(scope: string): Promise<string> {
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error('Missing REACT_APP_GOOGLE_OAUTH_CLIENT_ID.');
  }

  const oauth2 = await loadGoogleIdentityServices();

  return new Promise((resolve, reject) => {
    const tokenClient = oauth2.initTokenClient({
      client_id: clientId,
      scope,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        if (!response.access_token) {
          reject(new Error('No access token returned from Google.'));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: (error) => {
        reject(new Error(error.message || error.type || 'Google authorization was cancelled.'));
      },
    });
    tokenClient.requestAccessToken();
  });
}
